import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import clsx from 'clsx'
import {Button} from '../../../../../../_gori/partials/widgets'
import {OrdersConfig} from '../../../../orders/core/_const'

const ADDRESS_CHOICE = {
  SUGGESTED: 'suggested',
  ORIGINAL: 'original',
} as const

type AddressChoice = typeof ADDRESS_CHOICE[keyof typeof ADDRESS_CHOICE]

interface SelectedAddress {
  choice: AddressChoice
  addressData: Record<string, string>
}

interface VerifyAddressModalProps {
  show: boolean
  handleClose: () => void
  handleSave: (selectedAddress: SelectedAddress) => void
  dataSuggest: any
  originalAddress: any
  loading: boolean
}

const VerifyAddressModal: React.FC<VerifyAddressModalProps> = ({
  show,
  handleClose,
  handleSave,
  dataSuggest,
  originalAddress,
  loading,
}) => {
  const intl = useIntl()
  const [selectedChoice, setSelectedChoice] = useState<AddressChoice>(ADDRESS_CHOICE.SUGGESTED)

  const isAddressValid =
    dataSuggest?.verifications?.delivery?.success && dataSuggest?.verifications?.zip4?.success

  const handleSubmit = () => {
    const addressData =
      selectedChoice === ADDRESS_CHOICE.SUGGESTED
        ? OrdersConfig.VALIDATE_ADDRESS.reduce((acc, field) => {
            acc[field] = dataSuggest[field]
            return acc
          }, {} as Record<string, string>)
        : OrdersConfig.VALIDATE_ADDRESS.reduce((acc, field) => {
            acc[field] = originalAddress[field]
            return acc
          }, {} as Record<string, string>)

    handleSave({choice: selectedChoice, addressData})
  }

  return (
    <Modal
      show={show}
      centered
      dialogClassName='modal-size-lg h-auto'
      contentClassName='modal-padding'
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title bsPrefix='fw-bolder fs-1'>
          {intl.formatMessage({id: 'VERIFICATION_ADDRESS'})}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isAddressValid ? (
          <>
            <h5 className='text-dark mb-4'>{intl.formatMessage({id: 'VERIFIED_ADDRESS'})}</h5>
            <div className='row'>
              <div className='col-md-6'>
                <div
                  className={clsx('p-4 border rounded-2 cursor-pointer', {
                    'border-primary bg-light-primary': selectedChoice === ADDRESS_CHOICE.SUGGESTED,
                  })}
                  onClick={() => setSelectedChoice(ADDRESS_CHOICE.SUGGESTED)}
                >
                  <h5 className='text-dark'>{intl.formatMessage({id: 'SUGGESTED'})}</h5>
                  <div className='border rounded-2 p-3 bg-light'>
                    <div className='text-dark'>{dataSuggest.street1}</div>
                    <div className='text-dark'>
                      {`${dataSuggest.city}, ${dataSuggest.state} ${dataSuggest.zip}`}
                    </div>
                    <div className='text-dark'>{dataSuggest.country}</div>
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div
                  className={clsx('p-4 border rounded-2 cursor-pointer', {
                    'border-primary bg-light-primary': selectedChoice === ADDRESS_CHOICE.ORIGINAL,
                  })}
                  onClick={() => setSelectedChoice(ADDRESS_CHOICE.ORIGINAL)}
                >
                  <h5 className='text-dark'>{intl.formatMessage({id: 'ORIGINAL'})}</h5>
                  <div className='border rounded-2 p-3 bg-light'>
                    <div className='text-dark'>{originalAddress.street1}</div>
                    <div className='text-dark'>
                      {`${originalAddress.city}, ${originalAddress.state} ${originalAddress.zip}`}
                    </div>
                    <div className='text-dark'>{originalAddress.country}</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className='d-flex flex-fill justify-content-center'>
            <h3 className='text-center text-danger'>
              {intl.formatMessage({id: 'ADDRESS_IS_INVALID'})}
            </h3>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className='d-flex justify-content-end'>
          {isAddressValid ? (
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'SAVE'})}
              loadingText={intl.formatMessage({id: 'SAVE'})}
              loading={loading}
              disabled={loading}
              event={handleSubmit}
            />
          ) : (
            <Button
              className='btn btn-advanced-secondary'
              label={intl.formatMessage({id: 'CLOSE'})}
              event={handleClose}
            />
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export {VerifyAddressModal, ADDRESS_CHOICE}
